import Hls, { type ErrorData, Events, type Fragment, type HlsConfig } from 'hls.js';
import type { Rendition } from '@multiview/shared';
import { api } from '../api/client.ts';
import { platform } from '../platform.ts';
import * as audioBus from './audioBus.ts';
import * as serverLevels from './serverLevels.ts';
import * as sessionRelease from './sessionRelease.ts';
import { variantHint } from './quality.ts';

/** 'main', 'tile' or a reduced variant such as '720p50'. */
export type Quality = Rendition;

/** A load that was overtaken by a teardown of its layer; not a failure. */
class Superseded extends Error {
  constructor() {
    super('abgebrochen');
  }
}

/** Level drops within LEVEL_LOSS_WINDOW_MS after which a level is given up for LEVEL_BLOCK_MS. */
const MAX_LEVEL_LOSSES = 2;
const LEVEL_LOSS_WINDOW_MS = 2 * 60_000;
const LEVEL_BLOCK_MS = 5 * 60_000;

function qualityLabel(quality: Quality): string {
  if (quality === 'tile') return 'Kachelqualität';
  if (quality === 'main') return 'Volle Qualität';
  return `Qualität ${quality}`;
}

export interface TileStats {
  droppedFrames: number;
  totalFrames: number;
  bufferSeconds: number;
  /** Seconds behind the live edge, including any deliberate sync offset. */
  latencySeconds: number;
  width: number;
  height: number;
  level: number;
  mode: 'direct' | 'remux' | 'transcode' | null;
  /**
   * Wall-clock time of the frame on screen, from EXT-X-PROGRAM-DATE-TIME.
   * Null when the playlist carries no stamp.
   */
  wallClock: number | null;
  /** How far the picture lags real time, in seconds. */
  behindRealTime: number | null;
  /** Whether this tile's audio runs through the graph (and can be analysed). */
  audioActive: boolean;
  /** Linear RMS, or null when the audio graph is not carrying this tile. */
  rms: number | null;
  /** Rendition the proxy actually serves; 'main' when a variant fell back. */
  rendition: Quality | null;
  /**
   * Changes whenever the visible element is replaced or reloaded. Frame
   * counters are per element, so a delta across an epoch change is meaningless.
   */
  epoch: number;
}

export type TileState = 'idle' | 'loading' | 'playing' | 'error';

/** Baseline distance from the live edge. Three 2s segments is the usual safe gap. */
const BASE_LIVE_SYNC_SECONDS = 6;
/** Headroom above the sync target before hls.js considers the stream too far behind. */
const LATENCY_HEADROOM_SECONDS = 20;

/**
 * Seconds of played media kept behind the playhead. It must exceed the sync
 * offset, otherwise shifting a feed back in time has nothing to seek into.
 *
 * A desktop affords a flat two minutes. On a TV that is ~120 MB of SourceBuffer
 * per 1080p50 stream, a large share of what Chrome grants MSE there, and the
 * eviction it forces runs on the same main thread that feeds the decoders. So
 * smaller screens keep the offset plus a margin for the next nudge.
 */
function backBufferFor(syncOffsetSeconds: number): number {
  if (platform === 'desktop') return 120;
  return Math.ceil(syncOffsetSeconds) + 20;
}

/**
 * Media position of a wall-clock time within a playlist, or -1 (hls.js: the
 * usual live start) when the time is not covered or the playlist has no dates.
 */
function positionAt(fragments: Fragment[], at: number | null): number {
  if (at === null) return -1;
  for (const f of fragments) {
    const start = f.programDateTime;
    if (start === null) continue;
    if (at >= start && at < start + f.duration * 1000) return f.start + (at - start) / 1000;
  }
  return -1;
}

function hlsConfig(syncOffsetSeconds: number): Partial<HlsConfig> {
  const liveSync = BASE_LIVE_SYNC_SECONDS + syncOffsetSeconds;
  return {
    // The proxy emits plain 2s segments, so LL-HLS machinery buys nothing here.
    lowLatencyMode: false,
    // Transmuxing TS to fMP4 off the main thread, which also runs rendering.
    enableWorker: true,
    backBufferLength: backBufferFor(syncOffsetSeconds),
    maxBufferLength: 20,
    maxMaxBufferLength: 60,
    liveSyncDuration: liveSync,
    liveMaxLatencyDuration: liveSync + LATENCY_HEADROOM_SECONDS,
    // Speeding up playback to chase the live edge would fight the sync offset.
    maxLiveSyncPlaybackRate: 1,
    // No xhrSetup: playlist and segment requests are same-origin, so the session
    // cookie rides along on its own.
  };
}

interface Layer {
  video: HTMLVideoElement;
  hls: Hls | null;
  mode: TileStats['mode'];
  /** Rendition this layer was loaded with, so a reload asks for the same one. */
  quality: Quality | null;
  /** What the proxy actually serves for it (a variant may fall back to 'main'). */
  served: Quality | null;
  /** Bumped on teardown; a load that sees it change was overtaken. */
  token: number;
  /** Rejects a pending first-frame wait; a fatal error needs no 25 s timeout. */
  failWait: ((err: Error) => void) | null;
  /** Null while the audio graph is unavailable; `.muted` is used instead then. */
  audio: audioBus.TrackHandle | null;
  /** Proxy session behind this layer, whose measured levels it can use. */
  sessionId: string | null;
  /**
   * hls.js level index per rendition, when the layer plays the session's
   * master playlist. A quality change within it is a level switch -- seamless
   * at the next segment -- instead of a second stream in the other layer.
   */
  levels: Map<Quality, number> | null;
}

/**
 * One angle's playback.
 *
 * Two stacked video elements, one visible, exist so that changing quality -- what
 * happens whenever an angle is promoted to the main pane -- can load into the
 * hidden layer and cross-fade once it has a frame. A single element would have to
 * tear down its source and show black for the two seconds that takes.
 */
export class TilePlayer {
  readonly container: HTMLElement;
  #layers: [Layer, Layer];
  #active = 0;
  #channelId: number;
  #quality: Quality;
  #syncOffsetMs = 0;
  #muted = true;
  #destroyed = false;
  #retryTimer: number | null = null;
  #retries = 0;
  #switching = false;
  /** Rendition the engine asked for; differs from #quality while a switch is pending. */
  #wanted: Quality;
  #switchFailures = 0;
  #switchRetryTimer: number | null = null;
  /** Proxy sessions this player played; released to the proxy on destroy. */
  #heldSessions = new Set<string>();
  /**
   * How often hls.js dropped the wanted level lately, and the level given up
   * on after too many. An interlaced main copy, say, can fail in the browser
   * over and over; putting it back every perf tick then flips between it and
   * the tile, and the picture mostly stands still.
   */
  #levelLosses = 0;
  #lastLevelLossAt = 0;
  #blocked: { quality: Quality; until: number } | null = null;
  #epoch = 0;

  state: TileState = 'idle';
  onStateChange: ((state: TileState, detail?: string) => void) | null = null;

  constructor(container: HTMLElement, channelId: number, quality: Quality) {
    this.container = container;
    this.#channelId = channelId;
    this.#quality = quality;
    this.#wanted = quality;
    this.#layers = [this.#createLayer(0), this.#createLayer(1)];
  }

  #createLayer(index: number): Layer {
    const video = document.createElement('video');
    video.playsInline = true;
    video.muted = true;
    video.autoplay = true;
    video.preload = 'auto';
    // Keep the element out of the tab order; focus belongs to the tile wrapper.
    video.tabIndex = -1;
    // An idle hidden layer is not only transparent but `visibility:hidden`: an
    // element at opacity 0 still gets a compositor layer every frame, and on a
    // TV that is one more full-screen surface beside two running videos. It is
    // made visible (still transparent) only while a switch loads into it.
    video.style.cssText = [
      'position:absolute', 'inset:0', 'width:100%', 'height:100%',
      'object-fit:contain', 'background:#000',
      `opacity:${index === 0 ? 1 : 0}`,
      `visibility:${index === 0 ? 'visible' : 'hidden'}`,
      'transition:opacity 180ms linear',
      'pointer-events:none',
    ].join(';');
    this.container.appendChild(video);
    return { video, hls: null, mode: null, quality: null, served: null, token: 0, audio: null, failWait: null, sessionId: null, levels: null };
  }

  get activeVideo(): HTMLVideoElement {
    return this.#layers[this.#active]!.video;
  }

  get channelId(): number {
    return this.#channelId;
  }

  get quality(): Quality {
    return this.#quality;
  }

  get wantedQuality(): Quality {
    return this.#wanted;
  }

  #setState(state: TileState, detail?: string): void {
    this.state = state;
    this.onStateChange?.(state, detail);
  }

  async load(): Promise<void> {
    try {
      await this.#loadInto(this.#active, this.#quality, true);
    } catch (err) {
      if (this.#destroyed || err instanceof Superseded) return;
      this.#setState('error', err instanceof Error ? err.message : String(err));
      this.#scheduleReload(this.#active);
      return;
    }
    // The engine may have changed its mind while the first load ran.
    this.#pumpQuality();
  }

  /**
   * Asks for a rendition. The switch runs in the background and the newest
   * request wins, so calls never have to wait for -- or race -- each other.
   */
  requestQuality(quality: Quality): void {
    if (this.#destroyed) return;
    if (quality !== this.#wanted) this.#switchFailures = 0;
    this.#wanted = quality;
    this.#pumpQuality();
  }

  #pumpQuality(): void {
    if (this.#destroyed || this.#switching || this.state !== 'playing') return;
    if (this.#switchRetryTimer !== null) return;
    if (this.#wanted !== this.#quality) void this.switchQuality(this.#wanted);
  }

  /**
   * Loads a quality into the hidden layer and reveals it once it has decoded a
   * frame. Falls back to loading in place when the browser cannot do MSE.
   */
  async switchQuality(quality: Quality): Promise<void> {
    if (this.#destroyed || quality === this.#quality || this.#switching) return;
    if (this.#switchLevel(quality)) return;
    this.#switching = true;
    const target = this.#active === 0 ? 1 : 0;
    // Loading happens exactly as before -- transparent, but rendered -- so a
    // WebView has no reason to treat the incoming video as offscreen.
    this.#layers[target]!.video.style.visibility = 'visible';

    const from = this.#layers[this.#active]!;
    // Where the visible picture is, as wall-clock time of the segment playing.
    // Every rendition carries PROGRAM-DATE-TIME from the same session, so this
    // is the one position both layers agree on.
    const playingAt = () => from.hls?.playingDate?.getTime() ?? null;

    try {
      await this.#loadInto(target, quality, false, playingAt);
      if (this.#destroyed) return;
      await this.#alignWith(this.#layers[target]!, playingAt);
      if (this.#destroyed) return;

      this.#quality = quality;
      const previous = this.#active;
      this.#active = target;
      this.#epoch++;

      this.#layers[target]!.video.style.opacity = '1';
      this.#layers[previous]!.video.style.opacity = '0';
      this.#applyMuted();
      void this.#startPlayback(this.#layers[target]!.video);

      // Only tear the old layer down after the fade, so no frame is dropped.
      // A switch started meanwhile may already be loading into that layer.
      window.setTimeout(() => {
        if (!this.#destroyed && this.#active !== previous && !this.#switching) {
          this.#teardownLayer(previous);
          this.#layers[previous]!.video.style.visibility = 'hidden';
        }
      }, 220);
      this.#switchFailures = 0;
      // Clears a warning left by an earlier failed attempt.
      this.#setState('playing');
    } catch (err) {
      this.#teardownLayer(target);
      this.#layers[target]!.video.style.visibility = 'hidden';
      if (this.#destroyed || err instanceof Superseded) return;
      // The visible layer never stopped, so this is a warning, not an error
      // screen over a picture that is still running.
      const message = err instanceof Error ? err.message : String(err);
      this.#setState(this.state, `${qualityLabel(quality)} nicht verfügbar: ${message}`);
      this.#switchFailures++;
      if (this.#switchFailures <= 4) {
        this.#switchRetryTimer = window.setTimeout(() => {
          this.#switchRetryTimer = null;
          this.#pumpQuality();
        }, 3000 * this.#switchFailures);
      }
    } finally {
      this.#switching = false;
    }
    this.#pumpQuality();
  }

  /**
   * The way streaming services change quality: same player, same buffer, the
   * next segment simply comes from another rendition. hls.js's nextLevel keeps
   * the segment that is playing and replaces only what is buffered after it,
   * so the picture never stops -- unlike loading a second stream, which showed
   * as a stutter on every switch. All renditions come out of one ffmpeg, so
   * their timestamps agree. False when the rendition is not in the master
   * playlist (not encoded yet); the caller then takes the two-layer path,
   * which also asks the proxy to start it.
   */
  #switchLevel(quality: Quality): boolean {
    const layer = this.#layers[this.#active]!;
    const index = layer.levels?.get(quality);
    if (!layer.hls || index === undefined) return false;

    this.#quality = quality;
    layer.quality = quality;
    if (this.#isBlocked(quality)) {
      this.#pinFallback(layer, index);
      return true;
    }
    // Compared with the level hls.js is told to load, not the one playing: a
    // switch back while the old level still plays would otherwise be skipped
    // and leave it loading the other rendition for good.
    if (layer.hls.manualLevel !== index) layer.hls.nextLevel = index;
    this.#switchFailures = 0;
    this.#setState('playing');
    return true;
  }

  /**
   * hls.js drops manual level selection after any level error and lets its
   * own ABR choose, which can settle on the tile rendition under a full-screen
   * picture. #quality still reads as the wanted one, so nothing would ever ask
   * again. The engine calls this periodically to put the level back.
   */
  enforceLevel(): void {
    if (this.#destroyed || this.#switching) return;
    const layer = this.#layers[this.#active]!;
    const index = layer.levels?.get(this.#quality);
    if (!layer.hls || index === undefined) return;
    if (layer.hls.manualLevel === index) return;

    if (this.#isBlocked(this.#quality)) {
      this.#pinFallback(layer, index);
      return;
    }
    // Back on automatic means hls.js gave the level up after an error.
    if (layer.hls.manualLevel === -1) {
      const now = Date.now();
      // A level that then played for minutes was a one-off, not a pattern.
      this.#levelLosses = now - this.#lastLevelLossAt > LEVEL_LOSS_WINDOW_MS ? 1 : this.#levelLosses + 1;
      this.#lastLevelLossAt = now;
      if (this.#levelLosses >= MAX_LEVEL_LOSSES) {
        this.#blocked = { quality: this.#quality, until: now + LEVEL_BLOCK_MS };
        this.#pinFallback(layer, index);
        return;
      }
    }
    layer.hls.nextLevel = index;
  }

  #isBlocked(quality: Quality): boolean {
    if (!this.#blocked) return false;
    if (Date.now() >= this.#blocked.until) {
      // Time for another try; a setting may have changed meanwhile.
      this.#blocked = null;
      this.#levelLosses = 0;
      this.#setState(this.state);
      return false;
    }
    return this.#blocked.quality === quality;
  }

  /**
   * Keeps the layer on a level that plays instead of the blocked one: the one
   * playing now if it is another, else the next lower. Set manually, so hls.js's
   * own ABR cannot wander back to the blocked level either.
   */
  #pinFallback(layer: Layer, blockedIndex: number): void {
    const hls = layer.hls;
    if (!hls) return;
    const current = hls.currentLevel;
    const fallback = current >= 0 && current !== blockedIndex
      ? current
      : hls.levels
        .map((level, i) => ({ i, bitrate: level.bitrate }))
        .filter((l) => l.i !== blockedIndex)
        .sort((a, b) => b.bitrate - a.bitrate)[0]?.i;
    if (fallback === undefined) return;
    if (hls.manualLevel !== fallback) hls.nextLevel = fallback;
    const served = hls.levels[fallback]?.name as Quality | undefined;
    this.#setState(this.state, `${qualityLabel(this.#quality)} läuft hier nicht stabil, bleibe bei ${served ? qualityLabel(served) : 'niedrigerer Stufe'}`);
  }

  /**
   * @param startAt For a quality switch: the wall-clock time to start at, so
   *   the new layer continues where the old one is instead of at its own live
   *   position -- which could be several seconds away and showed as a jump.
   */
  async #loadInto(index: number, quality: Quality, primary: boolean, startAt?: () => number | null): Promise<void> {
    const layer = this.#layers[index]!;
    this.#teardownLayer(index);
    const token = layer.token;
    if (primary) {
      this.#setState('loading');
      this.#epoch++;
    }

    const resolved = await api.resolve(this.#channelId, quality, variantHint());
    if (this.#destroyed) return;
    // A layer torn down meanwhile -- a finished switch retiring it, say -- must
    // not come back to life as a hidden, still-decoding video.
    if (layer.token !== token) throw new Superseded();
    layer.mode = resolved.mode;
    layer.sessionId = resolved.sessionId;
    if (resolved.sessionId) serverLevels.retain(resolved.sessionId);
    if (resolved.sessionId && !this.#heldSessions.has(resolved.sessionId)) {
      this.#heldSessions.add(resolved.sessionId);
      sessionRelease.hold(resolved.sessionId);
    }
    layer.quality = quality;
    layer.served = resolved.rendition ?? quality;

    if (!Hls.isSupported()) {
      // Safari and some TV WebViews play HLS natively without MSE.
      layer.video.src = resolved.url;
      await this.#waitForFrame(layer);
      if (primary) this.#setState('playing');
      return;
    }

    const hls = new Hls({ ...hlsConfig(this.#syncOffsetMs / 1000), autoStartLoad: !startAt });
    layer.hls = hls;

    if (resolved.master) {
      hls.once(Events.MANIFEST_PARSED, (_e, data) => {
        const levels = new Map<Quality, number>();
        data.levels.forEach((level, i) => {
          if (level.name) levels.set(level.name as Quality, i);
        });
        layer.levels = levels;
        // Manual from the start: hls.js's own bandwidth logic would otherwise
        // pick a level, and this player knows better what the tile needs.
        const index = levels.get(layer.served ?? quality);
        if (index !== undefined) {
          hls.startLevel = index;
          hls.loadLevel = index;
        }
      });
      hls.on(Events.LEVEL_SWITCHED, (_e, data) => {
        const name = hls.levels[data.level]?.name;
        if (name) layer.served = name as Quality;
      });
    }

    if (startAt) {
      hls.once(Events.LEVEL_LOADED, (_e, data) => {
        hls.startLoad(positionAt(data.details.fragments, startAt()));
      });
    }

    hls.on(Events.ERROR, (_e, data) => this.#onHlsError(index, data));

    // Attach before loading. The other order lets MANIFEST_PARSED arrive while
    // the element still has no source, and a play() at that point rejects.
    hls.attachMedia(layer.video);
    hls.loadSource(resolved.master ?? resolved.url);

    await this.#waitForFrame(layer);
    if (this.#destroyed) return;

    // Start only once there is a decoded frame, and insist on it. Getting this
    // wrong is close to invisible: the tile shows its first frame and keeps
    // buffering, so it looks alive while standing still.
    await this.#startPlayback(layer.video);
    if (this.#destroyed) return;

    this.#retries = 0;
    if (primary) {
      // A fresh stream may well play the level an old one could not.
      this.#blocked = null;
      this.#levelLosses = 0;
    }
    // A switch has already been placed by startAt; applySyncOffset works on
    // the visible layer and would make that one jump to the live position.
    if (primary) {
      this.applySyncOffset(this.#syncOffsetMs);
      this.#setState('playing');
    }
  }

  /**
   * Fine correction before the reveal. The start position is only as exact as
   * the time loading took; whatever is left is closed by a seek inside what
   * the new layer has already buffered.
   */
  async #alignWith(layer: Layer, playingAt: () => number | null): Promise<void> {
    const want = playingAt();
    const have = layer.hls?.playingDate?.getTime() ?? null;
    if (want === null || have === null) return;
    const delta = (want - have) / 1000;
    if (Math.abs(delta) < 0.1 || Math.abs(delta) > 30) return;

    const { video } = layer;
    const target = video.currentTime + delta;
    let inside = false;
    for (let i = 0; i < video.buffered.length; i++) {
      if (target >= video.buffered.start(i) && target <= video.buffered.end(i) - 0.2) inside = true;
    }
    if (!inside) return;

    await new Promise<void>((resolve) => {
      const done = () => { clearTimeout(timer); video.removeEventListener('seeked', done); resolve(); };
      const timer = setTimeout(done, 600);
      video.addEventListener('seeked', done);
      video.currentTime = target;
    });
  }

  /**
   * play() rejects for reasons that are recoverable (a pending load, a
   * not-yet-settled autoplay decision), so one silent attempt is not enough.
   * Every tile is muted unless it holds audio focus, which is exactly the case
   * browsers do allow to autoplay.
   */
  async #startPlayback(video: HTMLVideoElement, attempts = 3): Promise<void> {
    for (let i = 0; i < attempts; i++) {
      if (this.#destroyed) return;
      try {
        await video.play();
        if (!video.paused) return;
      } catch {
        /* retried below */
      }
      await new Promise((r) => setTimeout(r, 150 * (i + 1)));
    }
  }

  /** Resolves on the first decoded frame, which is when a cross-fade is safe. */
  #waitForFrame(layer: Layer, timeoutMs = 25_000): Promise<void> {
    const { video } = layer;
    if (video.readyState >= 2 && video.videoWidth > 0) return Promise.resolve();

    return new Promise((resolve, reject) => {
      let settled = false;
      const done = (err?: Error) => {
        if (settled) return;
        settled = true;
        if (layer.failWait === done) layer.failWait = null;
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('canplay', onReady);
        clearTimeout(timer);
        err ? reject(err) : resolve();
      };
      const onReady = () => {
        if (video.videoWidth > 0 || video.readyState >= 3) done();
      };
      const timer = setTimeout(() => done(new Error('Stream lieferte kein Bild')), timeoutMs);
      layer.failWait = done;

      video.addEventListener('loadeddata', onReady);
      video.addEventListener('canplay', onReady);
    });
  }

  #onHlsError(index: number, data: ErrorData): void {
    const layer = this.#layers[index]!;
    if (!data.fatal) {
      // Buffer stalls and gaps are routine on IPTV and recover on their own.
      return;
    }

    if (data.type === Hls.ErrorTypes.MEDIA_ERROR && layer.hls && this.#retries < 2) {
      this.#retries++;
      layer.hls.recoverMediaError();
      return;
    }

    // Still waiting for its first frame: fail that wait now. Whoever started
    // the load -- a quality switch or the initial load -- handles the rest.
    if (layer.failWait) {
      layer.failWait(new Error(`${data.details}`));
      return;
    }

    // A hidden layer that already had its frame belongs to a switch that is
    // being abandoned; never reload it on its own.
    if (index !== this.#active) return;

    this.#setState('error', `${data.details}`);
    this.#scheduleReload(index);
  }

  /** Network errors on IPTV are usually transient; back off and rebuild. */
  #scheduleReload(index: number): void {
    if (this.#retries >= 5 || this.#destroyed) return;
    this.#retries++;
    const delay = Math.min(1000 * this.#retries, 5000);
    if (this.#retryTimer !== null) clearTimeout(this.#retryTimer);
    this.#retryTimer = window.setTimeout(() => {
      this.#retryTimer = null;
      if (this.#destroyed || index !== this.#active) return;
      const quality = this.#layers[index]!.quality ?? this.#quality;
      void this.#loadInto(index, quality, true)
        .then(() => this.#pumpQuality())
        .catch((err) => {
          if (this.#destroyed || err instanceof Superseded) return;
          this.#setState('error', err instanceof Error ? err.message : String(err));
          this.#scheduleReload(index);
        });
    }, delay);
  }

  /**
   * Shifts this angle back in time. Parallel feeds of the same match arrive
   * seconds apart, and only a faster feed can be held back, so the slowest feed
   * is the reference and everything else gets a positive offset.
   */
  applySyncOffset(offsetMs: number): void {
    this.#syncOffsetMs = Math.max(0, offsetMs);
    const seconds = this.#syncOffsetMs / 1000;
    const layer = this.#layers[this.#active]!;
    const { hls, video } = layer;

    if (hls) {
      const liveSync = BASE_LIVE_SYNC_SECONDS + seconds;
      hls.config.liveSyncDuration = liveSync;
      hls.config.liveMaxLatencyDuration = liveSync + LATENCY_HEADROOM_SECONDS;
      hls.config.backBufferLength = backBufferFor(seconds);

      const edge = hls.liveSyncPosition;
      if (edge !== null && Number.isFinite(edge)) {
        const target = edge - seconds;
        const seekable = video.seekable;
        const earliest = seekable.length > 0 ? seekable.start(0) : 0;
        if (target > earliest && Math.abs(video.currentTime - target) > 0.4) {
          video.currentTime = target;
        }
        return;
      }
    }

    const seekable = video.seekable;
    if (seekable.length > 0) {
      const target = seekable.end(seekable.length - 1) - BASE_LIVE_SYNC_SECONDS - seconds;
      if (target > seekable.start(0) && Math.abs(video.currentTime - target) > 0.4) {
        video.currentTime = target;
      }
    }
  }

  get syncOffsetMs(): number {
    return this.#syncOffsetMs;
  }

  setMuted(muted: boolean): void {
    this.#muted = muted;
    this.#applyMuted();
  }

  /**
   * Silence comes from the gain node, not from `muted`, because a muted element
   * delivers nothing to the analyser and the excitement detector would go deaf.
   * `muted` remains the fallback while the audio graph cannot run.
   */
  #applyMuted(): void {
    this.#layers.forEach((layer, i) => {
      // The hidden layer is loading a switch or fading out; it is never the
      // one to hear. Left to the gain/volume path it could start a new stream
      // at full volume -- with rapid switching two angles ended up audible.
      if (i !== this.#active) {
        layer.video.muted = true;
        return;
      }
      const audible = !this.#muted;

      layer.audio ??= audioBus.attach(layer.video);

      if (layer.audio) {
        // Routed through WebAudio: the element itself must stay unmuted or no
        // samples reach the graph at all.
        layer.video.muted = false;
        layer.audio.setGain(audible ? 1 : 0);
      } else {
        layer.video.muted = !audible;
      }
    });
  }

  /**
   * Retries joining the audio graph.
   *
   * The graph usually cannot start at mount time -- browsers keep an
   * AudioContext suspended until the user interacts -- and a single attempt
   * would therefore leave the tile permanently outside it, with no sound
   * analysis for the rest of the session.
   */
  ensureAudio(): void {
    if (this.#destroyed) return;
    if (this.#layers[this.#active]!.audio !== null) return;
    if (!audioBus.isRunning()) return;
    this.#applyMuted();
  }

  /**
   * Drops this player out of the audio graph and falls back to `.muted`.
   * Called when the graph is found to be stalling playback.
   */
  detachAudio(): void {
    for (const layer of this.#layers) {
      layer.audio?.release();
      layer.audio = null;
    }
    this.#applyMuted();
  }

  /** Media time, to detect a stalled player from outside. */
  get currentTime(): number {
    return this.activeVideo.currentTime;
  }

  /** Claims to be playing, so standing still would be a fault. */
  get shouldBeAdvancing(): boolean {
    const v = this.activeVideo;
    return !v.paused && v.readyState >= 3;
  }

  /** Whether the visible layer currently runs through the audio graph. */
  get usesAudioBus(): boolean {
    return this.#layers[this.#active]!.audio !== null;
  }

  /** Linear RMS of this angle's audio, or null when it cannot be measured. */
  level(): number | null {
    const layer = this.#layers[this.#active]!;
    if (serverLevels.serverLevelsActive) {
      const db = this.#serverDb(layer);
      return db === null ? null : 10 ** (db / 20);
    }
    layer.audio ??= audioBus.attach(layer.video);
    return layer.audio ? layer.audio.level() : null;
  }

  /** Level the proxy measured at the moment this layer is showing. */
  #serverDb(layer: Layer): number | null {
    const at = layer.hls?.playingDate?.getTime();
    if (!layer.sessionId || at === undefined) return null;
    return serverLevels.levelAt(layer.sessionId, at);
  }

  /** What the audio analysis hears, for the telemetry. */
  audioDiagnosis(): string {
    const layer = this.#layers[this.#active]!;
    if (serverLevels.serverLevelsActive) {
      if (!layer.sessionId) return 'srv ohne Session';
      const db = this.#serverDb(layer);
      return db === null ? 'srv keine Daten' : `srv ${Math.round(db)}dB`;
    }
    if (layer.audio) return layer.audio.describe();
    return audioBus.isRunning() ? 'nicht angebunden' : 'aus';
  }

  get muted(): boolean {
    return this.#muted;
  }

  setPaused(paused: boolean): void {
    const video = this.activeVideo;
    if (paused) video.pause();
    else void video.play().catch(() => {});
  }

  stats(): TileStats {
    const layer = this.#layers[this.#active]!;
    const { video, hls } = layer;
    const q = video.getVideoPlaybackQuality?.();
    const buffered = video.buffered;
    const bufferEnd = buffered.length > 0 ? buffered.end(buffered.length - 1) : video.currentTime;

    // hls.js derives this from EXT-X-PROGRAM-DATE-TIME; it is the only way to
    // know which real-world moment is currently on screen, and therefore the
    // only way to act on an outside event without running ahead of the picture.
    const wallClock = hls?.playingDate ?? null;

    let latency = 0;
    if (hls && hls.liveSyncPosition !== null && Number.isFinite(hls.liveSyncPosition)) {
      latency = Math.max(0, hls.liveSyncPosition - video.currentTime + BASE_LIVE_SYNC_SECONDS);
    } else {
      const seekable = video.seekable;
      if (seekable.length > 0) latency = Math.max(0, seekable.end(seekable.length - 1) - video.currentTime);
    }

    return {
      droppedFrames: q?.droppedVideoFrames ?? 0,
      totalFrames: q?.totalVideoFrames ?? 0,
      bufferSeconds: Math.max(0, bufferEnd - video.currentTime),
      latencySeconds: latency,
      width: video.videoWidth,
      height: video.videoHeight,
      level: hls?.currentLevel ?? -1,
      mode: layer.mode,
      wallClock: wallClock?.getTime() ?? null,
      behindRealTime: wallClock ? (Date.now() - wallClock.getTime()) / 1000 : null,
      audioActive: layer.audio !== null,
      rms: layer.audio ? layer.audio.level() : null,
      rendition: layer.served,
      epoch: this.#epoch,
    };
  }

  #teardownLayer(index: number): void {
    const layer = this.#layers[index]!;
    layer.token++;
    layer.failWait?.(new Superseded());
    layer.audio?.release();
    layer.audio = null;
    if (layer.sessionId) serverLevels.release(layer.sessionId);
    layer.sessionId = null;
    layer.levels = null;
    // Silent until #applyMuted decides otherwise for whatever loads next.
    layer.video.muted = true;
    if (layer.hls) {
      layer.hls.destroy();
      layer.hls = null;
    }
    layer.video.removeAttribute('src');
    layer.video.load();
    layer.mode = null;
    layer.quality = null;
    layer.served = null;
  }

  destroy(): void {
    this.#destroyed = true;
    if (this.#retryTimer !== null) clearTimeout(this.#retryTimer);
    if (this.#switchRetryTimer !== null) clearTimeout(this.#switchRetryTimer);
    this.#teardownLayer(0);
    this.#teardownLayer(1);
    for (const id of this.#heldSessions) sessionRelease.drop(id);
    this.#heldSessions.clear();
    for (const layer of this.#layers) layer.video.remove();
    this.#setState('idle');
  }
}
